import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function SeasideOffersPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedLocation, setSelectedLocation] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 16; // 4 rows × 4 columns
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if a category was passed from navigation
  useEffect(() => {
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory);
    }
  }, [location.state]);

  const categories = ['All', 'Days out', 'Food & Drink', 'Cinema', 'Shopping', 'Holidays', 'Free', 'Rainy Days'];

  const locations = ['All', 'Skegness', 'Ingoldmells', 'Mablethorpe', 'Chapel St Leonards', 'Friskney', 'Burgh Le Marsh', 'Trustthorpe', 'Louth', 'Ludborough', 'Hogsthorpe'];

  const categoryColors: Record<string, { default: string; active: string }> = {
    'All': { 
      default: 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200', 
      active: 'bg-cyan-500 text-white shadow-md' 
    },
    'Days out': { 
      default: 'bg-orange-100 text-orange-700 hover:bg-orange-200', 
      active: 'bg-orange-500 text-white shadow-md' 
    },
    'Food & Drink': { 
      default: 'bg-green-100 text-green-700 hover:bg-green-200', 
      active: 'bg-green-500 text-white shadow-md' 
    },
    'Cinema': { 
      default: 'bg-pink-100 text-pink-700 hover:bg-pink-200', 
      active: 'bg-pink-500 text-white shadow-md' 
    },
    'Shopping': { 
      default: 'bg-teal-100 text-teal-700 hover:bg-teal-200', 
      active: 'bg-teal-500 text-white shadow-md' 
    },
    'Holidays': { 
      default: 'bg-amber-100 text-amber-700 hover:bg-amber-200', 
      active: 'bg-amber-500 text-white shadow-md' 
    },
    'Free': { 
      default: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200', 
      active: 'bg-emerald-500 text-white shadow-md' 
    },
    'Rainy Days': { 
      default: 'bg-lime-100 text-lime-700 hover:bg-lime-200', 
      active: 'bg-lime-600 text-white shadow-md' 
    }
  };

  // Helper function to get category badge color
  const getCategoryBadgeColor = (category: string) => {
    return 'bg-cyan-100 text-cyan-700';
  };

  const offers = [
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/00f4176c2bf65e3da493bb1a779aafdf.jpeg', title: 'Silver Beach Todays Store', discount: '5% off (min spend £10)', category: 'Shopping', location: 'Ingoldmells' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/8e6d2a766ee43a478370c07573d69ae8.jpeg', title: 'Basils Coffee Shop & Restaurant', discount: '10% off food', category: 'Food & Drink', location: 'Burgh Le Marsh' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/6079edb0c0d54837644099f24ffdb06d.jpeg', title: 'Suncastle Skegness', discount: 'Kids eat & play £4.95', category: 'Days out', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/3ac33b38a577a4a99e179e4b5f49a1f5.jpeg', title: 'Hardys Animal Farm', discount: '10% off admission', category: 'Days out', location: 'Ingoldmells' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/cfabe4c07bfd5b3c2cbf6490269a9a21.jpeg', title: 'Rock City', discount: '10% off food and drink', category: 'Food & Drink', location: 'Ingoldmells' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/8351c78c279034d682b88352d4955523.jpeg', title: 'Jump Warriors Trampoline Park', discount: '10% off admission', category: 'Days out', location: 'Ingoldmells' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/744a5011438d709167c83b0437842ced.jpeg', title: 'Big Sky Alpacas', discount: '10% off alpaca walk', category: 'Days out', location: 'Trustthorpe' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/77e13852c146e45e848a691ddeb8783a.jpeg', title: 'Atlantic Fish Bar', discount: '20% off or free kids meal', category: 'Food & Drink', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/528f46319de5b0bcba99417cb2aef4fb.jpeg', title: 'Crafty Local Ale House', discount: '10% off draught products', category: 'Food & Drink', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/7684548e855aaf400912f13711b6626c.jpeg', title: 'Jump Warriors Trampoline Park', discount: '10% off admission', category: 'Rainy Days', location: 'Ingoldmells' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/08044d60feee403d2e1bcadca7c1201f.jpeg', title: 'The Candy Cove', discount: '15% off Pick n Mix', category: 'Shopping', location: 'Chapel St Leonards' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/ac66a2214b3758efec1b649f31eec589.jpeg', title: '2 Wild Retro', discount: '10% off', category: 'Shopping', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/593317f02d5e04c347191a1e948d2d97.jpeg', title: 'Kids world', discount: '20% off admission', category: 'Days out', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/a3597fc93cef2332930ccebeafc71709.jpeg', title: 'Kids world', discount: '20% off admission', category: 'Rainy Days', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/ef2839277314273996625cceeac156e4.jpeg', title: 'Lakeside Camping & Fisheries', discount: '£2 off day ticket', category: 'Days out', location: 'Louth' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/9e219251789cbb67c56281ec24b84158.jpeg', title: 'Empire Bowl - Skegness', discount: '20% off Bowling', category: 'Days out', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/fc44f19c825f197328eeea587b7fa79f.jpeg', title: 'Empire Bowl', discount: '20% off bowling', category: 'Rainy Days', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/aac2996087ec87c8fd02a0cc19bbd726.jpeg', title: 'Cheeky Blinders', discount: '50% off discount card', category: 'Food & Drink', location: 'Ingoldmells' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/48ffe454eb956c8b1255b0ff20bba1ce.jpeg', title: 'The Garrison', discount: '50% off discount card', category: 'Food & Drink', location: 'Ingoldmells' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/fbc15bd4c367c8f81aff33f9a12ab7d6.jpeg', title: 'The Coffee House', discount: '20% off or a slice of cake', category: 'Food & Drink', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/80e428eb555acc20a7b4b48ac510142c.jpeg', title: 'Lincolnshire Wildlife Park', discount: '15% off entry', category: 'Days out', location: 'Friskney' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/e3095f177b8f054f4eb710e48c64095d.jpeg', title: 'Skegness Natureland Seal Sanctuary', discount: '10% off admission', category: 'Days out', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/6003e3b0444feb75325efbaa610b0c21.jpeg', title: 'Lincolnshire Wolds Railway', discount: '10% off all ticket prices', category: 'Days out', location: 'Ludborough' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/26cb83d13f71b3e3796deeec55d411ad.webp', title: 'The Dunes Complex', discount: '10% off food and drink', category: 'Food & Drink', location: 'Mablethorpe' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/caa840a9677f2abea32e7d21330f4cfe.jpeg', title: 'Chacada Body Piercing Studio', discount: '£5 off piercings', category: 'Shopping', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/2ccd0a80b5efcd4e2f38af4d3678037b.jpeg', title: 'The Hideout Café', discount: '10% off food and drink', category: 'Food & Drink', location: 'Skegness' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/11532bd1e8f8475a7b28364b08c4a25d.jpeg', title: 'Queens Park', discount: 'Free to visit', category: 'Free', location: 'Mablethorpe' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/01ce8533e288ac41ee7032e32adf9f93.jpeg', title: 'The Coffee House By Little Nans', discount: '10% off food and drink', category: 'Food & Drink', location: 'Chapel St Leonards' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/74097cca6510ae8d201393485d8d615a.jpeg', title: 'Wren Farm', discount: '£1 off any waffle', category: 'Food & Drink', location: 'Hogsthorpe' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/74097cca6510ae8d201393485d8d615a.jpeg', title: 'Wren Farm', discount: 'Free animal feed bag', category: 'Days out', location: 'Hogsthorpe' },
    { img: 'https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/8cfa3b3a727ab6beae2f75536e8b4c4c.png', title: 'The Duck & Drake', discount: 'Free dessert with main meal', category: 'Food & Drink', location: 'Burgh Le Marsh' }
  ];

  const filteredOffers = offers.filter(offer => {
    const matchesCategory = selectedCategory === 'All' || offer.category === selectedCategory;
    const matchesSelectedLocation = selectedLocation === 'All' || offer.location === selectedLocation;
    return matchesCategory && matchesSelectedLocation;
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedLocation]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredOffers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOffers = filteredOffers.slice(startIndex, endIndex);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handleOfferClick = () => {
    const ctaSection = document.getElementById('ready-to-start-saving');
    if (ctaSection) {
      ctaSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'}`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/659671df2896ffc0ce475e0437197503.png" 
                alt="Seaside Vouchers" 
                className="h-16 w-16"
              />
              <img 
                src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/e0d98287e8c9dc8abdecc58291257ecc.png" 
                alt="SEASIDE VOUCHERS" 
                className="h-12"
              />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Home</Link>
              <Link to="/how-it-works" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">How it works</Link>
              <Link to="/seaside-offers" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Seaside Vouchers App</Link>
              <Link to="/voucher-book" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Voucher Book</Link>
              <Link to="/leisure-home-sales" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Leisure Home Sales</Link>
              <Link to="/accommodation" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Accommodation</Link>
              <Link to="/contact" className="text-sm font-semibold text-gray-900 hover:text-orange-500 transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-orange-500 after:transition-all after:duration-300 hover:after:w-full">Contact us</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 pb-8 bg-gradient-to-br from-orange-400 to-orange-500 overflow-hidden">
        {/* Animated Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-64 h-64 bg-yellow-300 rounded-full opacity-20 blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 bg-red-300 rounded-full opacity-20 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-white rounded-full opacity-10 blur-2xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center text-white mb-6">
            <h1 className="text-5xl font-bold mb-3 drop-shadow-lg">Seaside Vouchers App</h1>
            <p className="text-xl max-w-3xl mx-auto drop-shadow-md">Discover amazing deals and discounts at your favourite seaside destinations all in the palm of your hand</p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-6">Browse by Category</h2>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? categoryColors[category].active
                    : categoryColors[category].default
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Location Filter */}
      <section className="py-8 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-6">Browse by Location</h2>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {locations.map((loc) => (
              <button
                key={loc}
                onClick={() => setSelectedLocation(loc)}
                className={`px-6 py-2 rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                  selectedLocation === loc
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-orange-100 border border-gray-300'
                }`}
              >
                {loc}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Current Offers */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            {selectedCategory === 'All' && selectedLocation === 'All' 
              ? 'All Current Offers' 
              : selectedCategory !== 'All' && selectedLocation !== 'All'
              ? `${selectedCategory} Offers in ${selectedLocation}`
              : selectedCategory !== 'All'
              ? `${selectedCategory} Offers`
              : `Offers in ${selectedLocation}`}
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Download the app to unlock these exclusive deals
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {currentOffers.map((offer, index) => (
              <div 
                key={index} 
                onClick={handleOfferClick}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer border border-gray-200"
              >
                <div className="h-48 w-full overflow-hidden relative">
                  <img src={offer.img} alt={offer.title} className="w-full h-full object-cover object-center" />
                </div>
                <div className="p-4 text-center">
                  <span className={`inline-block px-2 py-1 text-xs font-semibold rounded mb-2 ${getCategoryBadgeColor(offer.category)}`}>
                    {offer.category}
                  </span>
                  <h3 className="text-base font-bold text-gray-900 mb-2">{offer.title}</h3>
                  <div className="flex items-center justify-center gap-1 text-sm text-gray-600 mb-2">
                    <i className="ri-map-pin-line text-orange-500"></i>
                    <span>{offer.location}</span>
                  </div>
                  <span className="inline-block px-3 py-2 bg-orange-100 text-orange-700 text-sm font-bold rounded-lg">
                    {offer.discount}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {filteredOffers.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-600 text-lg">No offers found</p>
              <p className="text-gray-500 text-sm mt-2">Try adjusting your search or filters</p>
            </div>
          )}

          {/* Pagination Controls */}
          {filteredOffers.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              {/* Back Button */}
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <i className="ri-arrow-left-s-line"></i> Back
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-2">
                {getPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === 'number' && setCurrentPage(page)}
                    disabled={page === '...'}
                    className={`min-w-[40px] h-10 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                      page === currentPage
                        ? 'bg-orange-500 text-white shadow-md cursor-pointer'
                        : page === '...'
                        ? 'bg-transparent text-gray-400 cursor-default'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              {/* Next Button */}
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Next <i className="ri-arrow-right-s-line"></i>
              </button>

              {/* End Button */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                End <i className="ri-skip-right-line"></i>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section id="ready-to-start-saving" className="py-8 bg-orange-500">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Start Saving?</h2>
          <p className="text-lg text-white mb-6">Download the app now and enjoy exclusive seaside offers</p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://apps.apple.com/pk/app/seaside-vouchers-user/id6745555629" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img 
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&amp;releaseDate=1726012800" 
                alt="Download on App Store" 
                className="h-14"
              />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.jeuxdevelopers.SeasideUser&pcampaignid=web_share&pli=1" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img 
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
                alt="Get it on Google Play" 
                className="h-14"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-cyan-500 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-6">
            <div>
              <img 
                src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/659671df2896ffc0ce475e0437197503.png" 
                alt="Seaside Vouchers" 
                className="h-20 w-20 transform hover:scale-110 transition-transform duration-300"
              />
              <img 
                src="https://static.readdy.ai/image/2b444193caba623045f9c59f793c0c65/e0d98287e8c9dc8abdecc58291257ecc.png" 
                alt="SEASIDE VOUCHERS" 
                className="h-10"
              />
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Quick Link</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Home</Link></li>
                <li><Link to="/how-it-works" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">How it works</Link></li>
                <li><Link to="/seaside-offers" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Seaside offers</Link></li>
                <li><Link to="/voucher-book" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Voucher Book</Link></li>
                <li><Link to="/leisure-home-sales" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Leisure Home Sales</Link></li>
                <li><Link to="/accommodation" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Accommodation</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://seasidevouchers.co.uk/privacy-policy/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Privacy &amp; cookie policy</a></li>
                <li><a href="https://seasidevouchers.co.uk/terms-conditions/" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">T&amp;C</a></li>
                <li><Link to="/contact" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Contact us</Link></li>
                <li><Link to="/business-registration" className="hover:text-orange-300 transition-all duration-300 cursor-pointer whitespace-nowrap hover:translate-x-1 inline-block">Add Your Business</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Follow Us</h4>
              <div className="flex gap-4">
                <a href="https://www.facebook.com/people/seasidevouchers/61566031950810/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://www.instagram.com/seaside_vouchers/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="https://www.tiktok.com/@seasidevouchers" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-all duration-300 cursor-pointer transform hover:scale-110 hover:-translate-y-1">
                  <i className="ri-tiktok-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-4 text-center text-sm">
            <p>Copyright © 2023 Seaside Voucher, All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
